import { MicVAD } from "@ricky0123/vad-web";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { dismissBanner, showBanner } from "../slices/bannerSlice";
import type { AppDispatch } from "../store";
import { useAppSelector } from "./redux-hooks";

const NO_SPEECH_TIMEOUT_MS = 30_000;
const BANNER_ID = "no-speech-detected";
const BANNER_MESSAGE =
  "We're not picking up any audio from your microphone. Please check that it isn't muted and your input volume is high enough.";

// Thresholds raised above library defaults (0.3 / 0.25) — defaults trip on
// HVAC, breathing, and keyboard noise, which would reset the silence timer
// and prevent the 30s banner from ever firing.
const VAD_OPTIONS = {
  baseAssetPath: "/vad/",
  onnxWASMBasePath: "/vad/",
  model: "v5" as const,
  positiveSpeechThreshold: 0.5,
  negativeSpeechThreshold: 0.35,
};

type VADInstance = Awaited<ReturnType<typeof MicVAD.new>>;

export function useNoSpeechDetection(
  isRecording: boolean,
  isPaused: boolean,
  mediaRecorder: MediaRecorder | null,
  enabled: boolean,
) {
  const dispatch = useDispatch<AppDispatch>();
  const bannerVisible = useAppSelector((s) => s.banner.banners.some((b) => b.id === BANNER_ID));

  const vadRef = useRef<VADInstance | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAlertedRef = useRef(false);
  const userDismissedRef = useRef(false);

  const clearSilenceTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showAlert = useCallback(() => {
    dispatch(
      showBanner({
        id: BANNER_ID,
        type: "warning",
        message: BANNER_MESSAGE,
        closable: true,
      }),
    );
  }, [dispatch]);

  const hideAlert = useCallback(() => {
    dispatch(dismissBanner(BANNER_ID));
  }, [dispatch]);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (hasAlertedRef.current || userDismissedRef.current) return;
    timerRef.current = setTimeout(() => {
      hasAlertedRef.current = true;
      showAlert();
    }, NO_SPEECH_TIMEOUT_MS);
  }, [clearSilenceTimer, showAlert]);

  const destroyVAD = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.destroy();
      vadRef.current = null;
    }
    clearSilenceTimer();
  }, [clearSilenceTimer]);

  useEffect(() => {
    const stream = mediaRecorder?.stream;

    if (!enabled || !isRecording || isPaused || !stream?.active) {
      return;
    }

    if (vadRef.current) {
      vadRef.current.start();
      armSilenceTimer();
      return;
    }

    // Scoped to this effect invocation: cleanup flips it before the next
    // run (or unmount), so a stale MicVAD.new() resolution destroys itself
    // instead of overwriting a freshly-created instance from the next run.
    let cancelled = false;

    MicVAD.new({
      ...VAD_OPTIONS,
      getStream: async () => stream,
      pauseStream: async () => {},
      resumeStream: async () => stream,
      onSpeechStart: () => {
        clearSilenceTimer();
        if (hasAlertedRef.current) {
          hasAlertedRef.current = false;
          hideAlert();
        }
      },
      onSpeechEnd: () => {
        armSilenceTimer();
      },
      onVADMisfire: () => {
        // Brief noise (cough, chair scrape) — do NOT reset the silence timer.
      },
    })
      .then((vad) => {
        if (cancelled) {
          vad.destroy();
          return;
        }
        vadRef.current = vad;
        vad.start();
        armSilenceTimer();
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: { feature: "vad_no_speech_detection" },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, isRecording, isPaused, mediaRecorder, armSilenceTimer, clearSilenceTimer, hideAlert]);

  useEffect(() => {
    if (isPaused && vadRef.current) {
      vadRef.current.pause();
      clearSilenceTimer();
    }
  }, [isPaused, clearSilenceTimer]);

  useEffect(() => {
    if (!isRecording && !isPaused && vadRef.current) {
      destroyVAD();
      hasAlertedRef.current = false;
      userDismissedRef.current = false;
      hideAlert();
    }
  }, [isRecording, isPaused, destroyVAD, hideAlert]);

  useEffect(() => {
    return () => {
      destroyVAD();
      hideAlert();
    };
  }, [destroyVAD, hideAlert]);

  // Sticky dismiss: if the banner disappears while we still expected it
  // showing, the user clicked X — don't re-arm this recording session.
  // Speech- and stop-driven dismissals reset hasAlertedRef before hiding,
  // so they skip this branch.
  useEffect(() => {
    if (!bannerVisible && hasAlertedRef.current) {
      userDismissedRef.current = true;
      hasAlertedRef.current = false;
    }
  }, [bannerVisible]);
}
